import React from 'react';

import { EditIcon } from '@vlabs/icons';
import { permissions } from '@vlabs/shared/config';
import { useModal } from '@vlabs/uikit';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { fetchLists } from '@vlabs/pages/lists/store';

import { ButtonWithPermissions } from '@vlabs/monokit/shared/ui/button-with-permissions';
import type { ButtonWithPermissionsProps } from '@vlabs/monokit/shared/ui/button-with-permissions';

import type { EditListProps } from './types';
import { ListForm } from '../../entities/lists';
import { useGetList, usePatchList } from '@vlabs/api/luna-api/client';
import type { UpdateList } from '@vlabs/api/luna-api/model';
import { buildTestid } from '../../shared/lib/utils';

interface EditListButtonProps extends
  EditListProps,
  Omit<ButtonWithPermissionsProps, 'onClick' | 'permissions'>,
  WithDataTestid {}

const displayName = 'EditListButton';
const testid = buildTestid(displayName);

function EditListButton({
  listId,
  onEditList,
  'data-testid': dataTestId = undefined,
  ...props
}: EditListButtonProps): React.JSX.Element {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const editListModal = useModal();
  const getList = useGetList(listId);
  const patchList = usePatchList();

  const onSubmit = (data: UpdateList) => {
    patchList.mutate({ listId, data }, {
      onSuccess: () => {
        fetchLists(dispatch);
        // обновляем данные внешнего списка или таблицы
        onEditList?.();
        editListModal.close();
        toast.success(t('lists:подтверждение.обновление.успех', { list: data.user_data }));
      },
    });
  };

  return (
    <React.Fragment>
      {editListModal.wrap(
        <ListForm
          context="update"
          defaultValues={{ user_data: getList.data?.user_data }}
          onCancel={editListModal.close}
          onSubmit={onSubmit}
        />,
      )}
      <ButtonWithPermissions
        data-testid={dataTestId ? testid(dataTestId)['data-testid'] : 'editCell'}
        icon={<EditIcon />}
        kind="primary"
        onClick={editListModal.open}
        permissions={{ rules: [permissions.list.modification] }}
        title={t('lists:widgets.edit list widget.button.edit button.title')}
        variant="dimmed"
        {...props}
      />
    </React.Fragment>

  );
}

EditListButton.displayName = displayName;

export { EditListButton };
